import { BaseWidgetFormFields } from 'components/consumer-page-widgets/BaseWidgetFormFields';
import { FormNumericField, FormSelectField, FormTextField } from 'ui';
import { FormikErrors, useFormikContext } from 'formik';
import { consumer } from '@tock/coretypes';

export interface GridWidgetFields extends BaseWidgetFormFields {
  title: string;
  subtitle: string;
  linkText: string;
  linkHref: string;
  dataType: '' | 'business' | 'experience';
  configId: number;
  pageSize: number;
}

interface Props {
  collectionDataConfigurations: consumer.CollectionDataConfiguration[];
  displayMode: consumer.GridWidgetDisplayType;
}

export const validateGridWidgetFormFields = (values: GridWidgetFields) => {
  const errors: FormikErrors<GridWidgetFields> = {};

  if (values.pageSize > 100) {
    errors.pageSize = 'Page size must be less than 100';
  }

  if (!values.name) {
    errors.name = 'Name is required';
  }

  if (!values.dataType) {
    errors.dataType = 'Data type is required';
  }

  if (!values.configId) {
    errors.configId = 'Data configuration is required';
  }

  return errors;
};

export const GridWidgetFormFields: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  collectionDataConfigurations,
  displayMode,
}) => {
  const {
    setFieldValue,
    values: { dataType },
  } = useFormikContext<GridWidgetFields>();
  return (
    <>
      <FormTextField id="title" name="title" label="Title" fullWidth />
      <FormTextField id="subtitle" name="subtitle" label="Subtitle (optional)" fullWidth />
      {/* Grids always have a "See more" button. This is a small signal the requirements for carousels and grids are divergent and these should be separate widgets. If more cases like this come up then consider splitting the widgets */}
      {displayMode === 'CAROUSEL' && (
        <>
          <FormTextField id="linkText" name="linkText" label="Link text" fullWidth />
          <FormTextField id="linkHref" name="linkHref" label="Link href" fullWidth />
        </>
      )}
      <FormSelectField
        id="dataType"
        name="dataType"
        label="Data type"
        fullWidth
        InputLabelProps={{ shrink: true }}
        onChange={() => setFieldValue('configId', -1)}
      >
        <option key="" value="" label="" />
        <option key="business" value="business" label="Business" />
        <option key="experiene" value="experience" label="Experience" />
      </FormSelectField>
      {dataType && (
        <FormSelectField
          id="configId"
          name="configId"
          label="Data Config"
          fullWidth
          InputLabelProps={{ shrink: true }}
        >
          <option key="" value={-1} label="" />
          {collectionDataConfigurations
            .filter((dataConfig) => {
              switch (dataType) {
                case 'business':
                  return (
                    dataConfig.dataConfiguration === 'businessLuceneSearch' ||
                    dataConfig.dataConfiguration === 'collectionConfig'
                  );
                case 'experience':
                  return dataConfig.dataConfiguration === 'experienceLuceneSearch';
                default:
                  return false;
              }
            })
            .map(({ id, name }) => (
              <option key={id} value={id} label={name} />
            ))}
        </FormSelectField>
      )}
      <FormNumericField
        id="pageSize"
        name="pageSize"
        label="Max list length"
        helperText="Default 25. This is the amount of results that will return with each click of the 'Load more' button. This cannot be greater than 100, and lower is better in terms of server load."
      />
    </>
  );
};
